import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Bus, 
  User, 
  CreditCard, 
  MapPin,
  Phone,
  Mail,
  Calendar,
  Save,
  Clock,
  Loader2,
  User2,
  History,
  FileDown,
  MessageCircle,
  FileText,
  Upload,
  Send,
  Trash2,
  Edit2,
  RefreshCw,
  Bell,
} from 'lucide-react';
import { BusCheckoutData, BusPassenger } from '../interfaces/BusCheckout';
import { CheckoutHistoryItem } from '../interfaces/Checkout';
import {
  busCheckoutService,
  BUS_EMISSION_PARTNERS,
  BusEmissionPartner,
  UpdateCheckoutBusFieldsDto,
  UpdateBusPassengerFieldsDto,
} from '../services/busCheckoutService';
import { paymentService, PaymentData } from '../services/checkoutService';
import { useAuth } from '../contexts/AuthContext';
import { DefaultLoader, BusCompletionModal, BusCompletionData } from '../components';
import { PermissionAlert } from '../components/PermissionAlert';
import { generateBusTicketPDF } from '../utils/generateBusTicketPDF';

const BusEmissionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkout, setCheckout] = useState<BusCheckoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [history, setHistory] = useState<CheckoutHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentData[]>([]);
  const [whatsappEvents, setWhatsappEvents] = useState<any>(null);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  const [boardingPasses, setBoardingPasses] = useState<any[]>([]);
  const [loadingBoardingPasses, setLoadingBoardingPasses] = useState(false);
  const [uploadingBp, setUploadingBp] = useState(false);
  const [sendingBp, setSendingBp] = useState<string | null>(null);

  // Edição de campos do checkout
  const [showFieldsPanel, setShowFieldsPanel] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsReason, setFieldsReason] = useState('');
  const [fieldDatetimeIda, setFieldDatetimeIda] = useState('');
  const [fieldChegadaIda, setFieldChegadaIda] = useState('');
  const [fieldVoltaDatetimeIda, setFieldVoltaDatetimeIda] = useState('');
  const [fieldVoltaChegadaIda, setFieldVoltaChegadaIda] = useState('');
  const [fieldBaseprice, setFieldBaseprice] = useState('');
  const [fieldBasepriceMk, setFieldBasepriceMk] = useState('');
  const [fieldEmissionPartner, setFieldEmissionPartner] = useState<BusEmissionPartner | ''>('');
  const [fieldVoltaEmissionPartner, setFieldVoltaEmissionPartner] = useState<BusEmissionPartner | ''>('');
  const [fieldReservedCode, setFieldReservedCode] = useState('');
  const [fieldVoltaReservedCode, setFieldVoltaReservedCode] = useState('');

  // Edição de passageiro
  const [editingPassenger, setEditingPassenger] = useState<BusPassenger | null>(null);
  const [passengerReason, setPassengerReason] = useState('');
  const [paxBirthDate, setPaxBirthDate] = useState('');
  const [paxName, setPaxName] = useState('');
  const [paxRg, setPaxRg] = useState('');
  const [paxCpf, setPaxCpf] = useState('');
  const [paxPhone, setPaxPhone] = useState('');
  const [paxSeat, setPaxSeat] = useState('');
  const [paxVoltaSeat, setPaxVoltaSeat] = useState('');
  const [savingPassenger, setSavingPassenger] = useState(false);

  // Reenvio de notificações
  const [showNotifyPanel, setShowNotifyPanel] = useState(false);
  const [sendingNotify, setSendingNotify] = useState(false);
  const [notifyChannels, setNotifyChannels] = useState<Record<string, boolean>>({ email: true, whatsapp: false, sms: false });
  const [notifyTemplate, setNotifyTemplate] = useState('');
  const [notifyReason, setNotifyReason] = useState('');


  const statusOptions = [
    { value: 'emission started', label: 'Emissão Iniciada' },
    { value: 'awaiting payment', label: 'Aguardando Pagamento' },
    { value: 'completed', label: 'Completado' },
    { value: 'cancelled', label: 'Cancelado' },
    { value: 'failed', label: 'Falhou' },
  ];

  const removeHyphens = (text: string | undefined) => text?.replace(/-/g, ' ') || '';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (checkout && id) {
      loadHistory();
    }
  }, [checkout, id]);



  useEffect(() => {
    if (id) {
      loadCheckoutDetails();
    }
  }, [id]);

  const loadCheckoutDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError('');
      const response = await busCheckoutService.getBusCheckoutById(id);
      setCheckout(response);
      setNewStatus(response.status);

      try {
        const payments = await paymentService.getPaymentsByCheckout(response?.pedido_id);
        setPaymentDetails(payments);
      } catch {}

      try {
        setLoadingWhatsapp(true);
        const events = await busCheckoutService.getWhatsappEvents(id);
        setWhatsappEvents(events);
      } catch {} finally {
        setLoadingWhatsapp(false);
      }

      // Carregar boarding passes se completed
      if (response.status === 'completed') {
        await loadBoardingPasses();
      }
    } catch (err: any) {
      console.error('Erro ao carregar detalhes do checkout:', err);
      const message = err.response?.data?.message || 'Erro ao carregar detalhes da emissão';
      setError(message);
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const loadBoardingPasses = async () => {
    if (!id) return;
    try {
      setLoadingBoardingPasses(true);
      const bps = await busCheckoutService.getBoardingPasses(id);
      setBoardingPasses(bps);
    } catch (err) {
      console.warn('Não foi possível carregar boarding passes:', err);
    } finally {
      setLoadingBoardingPasses(false);
    }
  };

  const handleSendBoardingPass = async (boardingPassId: string) => {
    if (!id) return;
    try {
      setSendingBp(boardingPassId);
      await busCheckoutService.sendBoardingPass(id, boardingPassId);
      await loadBoardingPasses();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao enviar cartão de embarque';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setSendingBp(null);
    }
  };

  const handleDeleteBoardingPass = async (boardingPassId: string) => {
    if (!id || !window.confirm('Deseja realmente remover este cartão de embarque?')) return;
    try {
      await busCheckoutService.deleteBoardingPass(id, boardingPassId);
      await loadBoardingPasses();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao remover cartão de embarque';
      setErrorMessage(message);
      setShowErrorAlert(true);
    }
  };

  const loadHistory = async () => {
    if (!id) return;
    
    try {
      setLoadingHistory(true);
      const historyData = await busCheckoutService.getCheckoutHistory(id);
      setHistory(historyData);
    } catch (err: any) {
      console.warn('Não foi possível carregar histórico:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!checkout || !newStatus || newStatus === checkout.status) return;

    // Se o novo status for 'completed', abrir modal
    if (newStatus === 'completed') {
      setShowCompletionModal(true);
      return;
    }

    try {
      setUpdating(true);
      await busCheckoutService.updateCheckoutStatus(checkout.id.toString(), newStatus, user?.username || 'Sistema');
      
      setCheckout({ ...checkout, status: newStatus as any });
      loadHistory();
    } catch (err: any) {
      console.error('Erro ao atualizar status:', err);
      const message = err.response?.data?.message || 'Erro ao atualizar status';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const handleCompletionConfirm = async (data: BusCompletionData) => {
    if (!checkout) return;

    try {
      setUpdating(true);

      const { boardingPassFiles, ...statusData } = data;

      // 1. Upload dos PDFs de cartão de embarque ANTES de mudar o status,
      //    para que o WhatsApp disparado pelo backend já encontre o boarding pass.
      if (boardingPassFiles && boardingPassFiles.length > 0) {
        for (const bp of boardingPassFiles) {
          const formData = new FormData();
          formData.append('file', bp.file);
          formData.append('passengerName', bp.passengerName);
          if (bp.seat) formData.append('seat', bp.seat);
          formData.append('uploadedBy', user?.username || 'admin');

          try {
            await busCheckoutService.uploadBoardingPass(checkout.id.toString(), formData);
          } catch (uploadErr: any) {
            console.error(`Erro ao fazer upload do boarding pass para ${bp.passengerName}:`, uploadErr);
          }
        }
        await loadBoardingPasses();
      }

      // 2. Atualizar status para completed (dispara o WhatsApp com o PDF já salvo)
      await busCheckoutService.updateCheckoutStatus(
        checkout.id.toString(), 
        'completed', 
        user?.username || 'Sistema',
        statusData
      );
      
      setCheckout({ ...checkout, status: 'completed' as any });
      setShowCompletionModal(false);
      loadHistory();
    } catch (err: any) {
      console.error('Erro ao atualizar status:', err);
      const message = err.response?.data?.message || 'Erro ao atualizar status';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentStatusUpdate = async (paymentStatus: 'processando' | 'aprovado' | 'reprovado' | 'reembolsado') => {
    if (!checkout || paymentStatus === checkout.paymentStatus) return;

    try {
      setUpdating(true);
      await busCheckoutService.updatePaymentStatus(checkout.id.toString(), paymentStatus, user?.username || 'Sistema');
      
      setCheckout({ ...checkout, paymentStatus });
      loadHistory();
    } catch (err: any) {
      console.error('Erro ao atualizar status do pagamento:', err);
      const message = err.response?.data?.message || 'Erro ao atualizar status do pagamento';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setUpdating(false);
    }
  };

  const openFieldsPanel = () => {
    if (!checkout) return;
    setFieldDatetimeIda(checkout.datetime_ida ?? '');
    setFieldChegadaIda(checkout.chegada_ida ?? '');
    setFieldVoltaDatetimeIda(checkout.volta_datetime_ida ?? '');
    setFieldVoltaChegadaIda(checkout.volta_chegada_ida ?? '');
    setFieldBaseprice(checkout.baseprice ?? '');
    setFieldBasepriceMk(checkout.baseprice_markup ?? '');
    setFieldEmissionPartner((checkout.emission_partner as BusEmissionPartner) ?? '');
    setFieldVoltaEmissionPartner((checkout.volta_emission_partner as BusEmissionPartner) ?? '');
    setFieldReservedCode(checkout.reserved_code_company ?? '');
    setFieldVoltaReservedCode(checkout.volta_reserved_code_company ?? '');
    setFieldsReason('');
    setShowFieldsPanel(true);
  };

  const handleSaveFields = async () => {
    if (!checkout || !fieldsReason.trim()) {
      setErrorMessage('Informe o motivo da alteração antes de salvar.');
      setShowErrorAlert(true);
      return;
    }
    try {
      setSavingFields(true);
      const dto: UpdateCheckoutBusFieldsDto = {
        updatedBy: user?.username || 'Sistema',
        reason: fieldsReason,
      };
      if (fieldDatetimeIda !== checkout.datetime_ida) dto.datetime_ida = fieldDatetimeIda;
      if (fieldChegadaIda !== checkout.chegada_ida) dto.chegada_ida = fieldChegadaIda;
      if (fieldVoltaDatetimeIda !== checkout.volta_datetime_ida) dto.volta_datetime_ida = fieldVoltaDatetimeIda;
      if (fieldVoltaChegadaIda !== checkout.volta_chegada_ida) dto.volta_chegada_ida = fieldVoltaChegadaIda;
      if (fieldBaseprice && fieldBaseprice !== checkout.baseprice) dto.baseprice = parseFloat(fieldBaseprice);
      if (fieldBasepriceMk && fieldBasepriceMk !== checkout.baseprice_markup) dto.baseprice_markup = parseFloat(fieldBasepriceMk);
      if (fieldEmissionPartner && fieldEmissionPartner !== checkout.emission_partner) dto.emission_partner = fieldEmissionPartner;
      if (fieldVoltaEmissionPartner && fieldVoltaEmissionPartner !== checkout.volta_emission_partner) dto.volta_emission_partner = fieldVoltaEmissionPartner;
      if (fieldReservedCode !== checkout.reserved_code_company) dto.reserved_code_company = fieldReservedCode;
      if (fieldVoltaReservedCode !== checkout.volta_reserved_code_company) dto.volta_reserved_code_company = fieldVoltaReservedCode;

      await busCheckoutService.updateCheckoutFields(checkout.id.toString(), dto);
      await loadCheckoutDetails();
      loadHistory();
      setShowFieldsPanel(false);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao atualizar campos';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setSavingFields(false);
    }
  };

  const openPassengerEdit = (p: BusPassenger) => {
    setEditingPassenger(p);
    setPaxName(p.name);
    setPaxBirthDate(p.birth_date ?? '');
    setPaxRg(p.rg ?? '');
    setPaxCpf(p.cpf ?? '');
    setPaxPhone(p.phone_number ?? '');
    setPaxSeat((p as any).seat_number ?? '');
    setPaxVoltaSeat((p as any).volta_seat_number ?? '');
    setPassengerReason('');
  };

  const handleSavePassenger = async () => {
    if (!checkout || !editingPassenger || !passengerReason.trim()) {
      setErrorMessage('Informe o motivo da alteração antes de salvar.');
      setShowErrorAlert(true);
      return;
    }
    try {
      setSavingPassenger(true);
      const dto: UpdateBusPassengerFieldsDto = {
        updatedBy: user?.username || 'Sistema',
        reason: passengerReason,
      };
      if (paxName !== editingPassenger.name) dto.name = paxName;
      if (paxBirthDate !== editingPassenger.birth_date) dto.birth_date = paxBirthDate;
      if (paxRg !== editingPassenger.rg) dto.rg = paxRg;
      if (paxCpf !== editingPassenger.cpf) dto.cpf = paxCpf;
      if (paxPhone !== editingPassenger.phone_number) dto.phone_number = paxPhone;
      if (paxSeat !== (editingPassenger as any).seat_number) dto.seat_number = paxSeat;
      if (paxVoltaSeat !== (editingPassenger as any).volta_seat_number) dto.volta_seat_number = paxVoltaSeat;

      await busCheckoutService.updatePassengerFields(checkout.id.toString(), editingPassenger.id, dto);
      await loadCheckoutDetails();
      loadHistory();
      setEditingPassenger(null);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao atualizar passageiro';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setSavingPassenger(false);
    }
  };

  const handleResendNotification = async () => {
    if (!checkout) return;
    const channels = (Object.entries(notifyChannels)
      .filter(([, v]) => v)
      .map(([k]) => k)) as ('email' | 'sms' | 'whatsapp')[];

    if (channels.length === 0) {
      setErrorMessage('Selecione ao menos um canal para reenviar.');
      setShowErrorAlert(true);
      return;
    }
    try {
      setSendingNotify(true);
      await busCheckoutService.resendNotification(checkout.id.toString(), {
        channels,
        template: notifyTemplate || undefined,
        updatedBy: user?.username || 'Sistema',
        reason: notifyReason || undefined,
      });
      loadHistory();
      setShowNotifyPanel(false);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erro ao reenviar notificações';
      setErrorMessage(message);
      setShowErrorAlert(true);
    } finally {
      setSendingNotify(false);
    }
  };

  const formatCurrency = (value: string | null) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(parseFloat(value));
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    const normalized = dateString.replace(' ', 'T');
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'emission started': 'badge-pending',
      'awaiting payment': 'badge-processing',
      'completed': 'badge-completed',
      'cancelled': 'badge-cancelled',
      'failed': 'badge-failed',
    };

    const labelMap: { [key: string]: string } = {
      'emission started': 'Emissão Iniciada',
      'awaiting payment': 'Aguardando Pagamento',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };

    return (
      <span className={`badge ${statusMap[status] || 'badge-pending'}`}>
        {labelMap[status] || status}
      </span>
    );
  };

  const getStatusLabel = (status: string) => {
    const labelMap: { [key: string]: string } = {
      'emission started': 'Emissão Iniciada',
      'awaiting payment': 'Aguardando Pagamento',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };
    return labelMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'emission started': 'var(--color-blue)',
      'awaiting payment': 'var(--color-orange)',
      'completed': 'var(--color-green)',
      'cancelled': 'var(--color-red)',
      'failed': 'var(--color-red)',
    };
    return colorMap[status] || 'var(--color-darkgray)';
  };

  const getPartnerLabel = (value: string | undefined) => {
    if (!value) return '—';
    return BUS_EMISSION_PARTNERS.find(p => p.value === value)?.label ?? value;
  };

  const getHistoryIcon = (item: CheckoutHistoryItem) => {
    switch (item.action) {
      case 'field_update':      return <Edit2 size={14} color="white" />;
      case 'notification_resent': return <Bell size={14} color="white" />;
      default:                  return <Clock size={14} color="white" />;
    }
  };

  const getHistoryColor = (item: CheckoutHistoryItem) => {
    switch (item.action) {
      case 'field_update':        return '#7c3aed';
      case 'notification_resent': return '#0284c7';
      default: return getStatusColor(item.status ?? '');
    }
  };

  const getHistoryTitle = (item: CheckoutHistoryItem) => {
    switch (item.action) {
      case 'field_update': {
        const fieldLabels: Record<string, string> = {
          datetime_ida: 'Data de Embarque (Ida)',
          chegada_ida: 'Data de Chegada (Ida)',
          volta_datetime_ida: 'Data de Embarque (Volta)',
          volta_chegada_ida: 'Data de Chegada (Volta)',
          baseprice: 'Preço Base',
          baseprice_markup: 'Preço com Markup',
          emission_partner: 'Parceiro de Emissão (Ida)',
          volta_emission_partner: 'Parceiro de Emissão (Volta)',
          reserved_code_company: 'Código de Reserva (Ida)',
          volta_reserved_code_company: 'Código de Reserva (Volta)',
          vehicle_plate: 'Placa (Ida)',
          volta_vehicle_plate: 'Placa (Volta)',
          name: 'Nome do Passageiro',
          birth_date: 'Data de Nascimento',
          rg: 'RG',
          cpf: 'CPF',
          phone_number: 'Telefone',
          seat_number: 'Assento (Ida)',
          volta_seat_number: 'Assento (Volta)',
        };
        const label = item.fieldName ? (fieldLabels[item.fieldName] ?? item.fieldName) : 'Campo';
        const suffix = item.passengerId ? ` — Pax #${item.passengerId}` : '';
        return `Edição de Campo: ${label}${suffix}`;
      }
      case 'notification_resent':
        return `Notificação Reenviada: ${item.notificationType ?? '—'}`;
      default:
        return getStatusLabel(item.status ?? '');
    }
  };

  if (loading) {
    return <DefaultLoader title="Detalhes da Emissão" messages={['Carregando detalhes...', 'Processando informações...']} />;
  }

  if (error || !checkout) {
    return (
      <div>
        <button onClick={() => navigate('/bus-emissions')} className="btn btn-secondary mb-3">
          <ArrowLeft size={16} />
          Voltar
        </button>
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: 'var(--color-red)', marginBottom: '16px' }}>{error || 'Emissão não encontrada'}</p>
          <button onClick={() => navigate('/bus-emissions')} className="btn btn-primary">
            Voltar para Emissões
          </button>
        </div>
      </div>
    );
  }


  return (
    <div style={{ position: 'relative' }}>
      {updating && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px 32px',
            borderRadius: 'var(--br-8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            border: '1px solid var(--color-gainsboro-100)'
          }}>
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--color-blue)' }} />
            <div style={{
              fontSize: 'var(--font-size-16)',
              fontWeight: 500,
              color: 'var(--color-blue)'
            }}>
              Atualizando status da emissão...
            </div>
            <div style={{
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-darkgray)',
              textAlign: 'center'
            }}>
              Aguarde enquanto processamos a alteração
            </div>
          </div>
        </div>
      )}
      
      <div className="d-flex justify-between align-center mb-4">
        <div className="d-flex align-center gap-2">
          <button onClick={() => navigate('/bus-emissions')} className="btn btn-secondary">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 style={{ fontSize: 'var(--font-size-24)', fontWeight: 600, margin: '0 0 4px 0' }}>
              Detalhes da Emissão de Ônibus
            </h2>
            <p style={{ fontSize: 'var(--font-size-14)', color: 'var(--color-darkgray)', margin: 0 }}>
              Pedido: {checkout.pedido_id}
            </p>
          </div>
        </div>
        <div className="d-flex align-center gap-3">
          {(checkout.status === 'emission started' || updating) && (
            <div className="d-flex flex-column align-center" style={{
              padding: '12px 16px',
              backgroundColor: updating ? 'var(--color-orange)' : 'var(--color-blue)',
              color: 'white',
              borderRadius: 'var(--br-5)',
              fontSize: 'var(--font-size-12)',
              fontWeight: 500,
              textAlign: 'center',
              minWidth: '180px'
            }}>
              <div style={{ marginBottom: '4px', fontSize: 'var(--font-size-11)', opacity: 0.9 }}>
                Iniciado por: {user?.username || 'N/A'}
              </div>
              <div style={{ marginBottom: '6px', fontSize: 'var(--font-size-11)', opacity: 0.9 }}>
                {currentTime.toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}
              </div>
              <span>{updating ? 'Atualizando às' : 'Emissão iniciada às'}</span>
              <span style={{ fontWeight: 600 }}>
                {currentTime.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </span>
            </div>
          )}
          <button
            onClick={() => { generateBusTicketPDF(checkout); }}
            className="btn btn-secondary"
            title="Gerar PDF do ticket de embarque"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <FileDown size={16} />
            Ticket PDF
          </button>
          {getStatusBadge(checkout.status)}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px' }}>
        <div>
          {/* Informações da Viagem de Ida */}
          <div className="card mb-3">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bus size={20} color="var(--color-blue)" />
              Viagem de Ida
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr auto 1fr', 
              gap: '24px', 
              alignItems: 'center', 
              marginBottom: '20px',
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px'
            }}>
              <div>
                <div style={{ 
                  fontSize: 'var(--font-size-12)', 
                  color: 'var(--color-darkgray)', 
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}>
                  Origem
                </div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: '20px', 
                  marginBottom: '4px',
                  color: 'var(--color-secondary)',
                  textTransform: 'capitalize'
                }}>
                  {removeHyphens(checkout.cidade_origem_slug)}
                </div>
                <div style={{ 
                  fontSize: 'var(--font-size-14)', 
                  color: 'var(--color-darkgray)',
                  marginBottom: '12px',
                  textTransform: 'capitalize'
                }}>
                  {removeHyphens(checkout.origem_slug)}
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: 'var(--font-size-14)', 
                  color: 'var(--color-blue)',
                  fontWeight: 500
                }}>
                  <Clock size={14} />
                  {formatDate(checkout.datetime_ida)}
                </div>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-blue)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Bus size={20} color="white" />
                </div>
                <div style={{
                  width: '60px',
                  height: '2px',
                  backgroundColor: 'var(--color-gray)',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    right: '-4px',
                    top: '-3px',
                    width: '8px',
                    height: '8px',
                    borderTop: '2px solid var(--color-gray)',
                    borderRight: '2px solid var(--color-gray)',
                    transform: 'rotate(45deg)'
                  }} />
                </div>
              </div>
              
              <div style={{ textAlign: 'right' }}>
                <div style={{ 
                  fontSize: 'var(--font-size-12)', 
                  color: 'var(--color-darkgray)', 
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: '0.5px'
                }}>
                  Destino
                </div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: '20px', 
                  marginBottom: '4px',
                  color: 'var(--color-secondary)',
                  textTransform: 'capitalize'
                }}>
                  {removeHyphens(checkout.cidade_destino_slug)}
                </div>
                <div style={{ 
                  fontSize: 'var(--font-size-14)', 
                  color: 'var(--color-darkgray)',
                  marginBottom: '12px',
                  textTransform: 'capitalize'
                }}>
                  {removeHyphens(checkout.destino_slug)}
                </div>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  fontSize: 'var(--font-size-14)', 
                  color: 'var(--color-blue)',
                  fontWeight: 500
                }}>
                  <Clock size={14} />
                  {formatDate(checkout.chegada_ida)}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)' }}>
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Empresa</div>
                <div style={{ fontWeight: 500 }}>{checkout.company_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Tipo de Assento</div>
                <div style={{ fontWeight: 500 }}>{checkout.tipo_assento}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Tipo de Veículo</div>
                <div style={{ fontWeight: 500 }}>{checkout.tipo_veiculo}</div>
              </div>
              {checkout.reserved_code_company && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Código de Reserva</div>
                  <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>{checkout.reserved_code_company}</div>
                </div>
              )}
              {checkout.vehicle_plate && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Placa do Veículo</div>
                  <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>{checkout.vehicle_plate}</div>
                </div>
              )}
              {checkout.emission_partner && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Parceiro de Emissão</div>
                  <div style={{ fontWeight: 600, color: 'var(--color-blue)' }}>{getPartnerLabel(checkout.emission_partner)}</div>
                </div>
              )}
              {checkout.emitted_total_price != null && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Preço Total Emitido</div>
                  <div style={{ fontWeight: 700, color: 'var(--color-green)', fontFamily: 'monospace' }}>
                    R$ {Number(checkout.emitted_total_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              )}
            </div>

            {/* Conexão - Timeline */}
            {checkout.is_conexao !== undefined && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <MapPin size={14} color="var(--color-darkgray)" />
                  <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Rota da Viagem
                  </span>
                  <span style={{
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: 'var(--font-size-12)',
                    fontWeight: 600,
                    backgroundColor: checkout.is_conexao ? '#fef3c7' : '#dcfce7',
                    color: checkout.is_conexao ? '#92400e' : '#166534',
                  }}>
                    {checkout.is_conexao ? `${(checkout.conexoes?.length || 0) + 1} trechos` : 'Direto'}
                  </span>
                </div>

                {/* Timeline visual */}
                <div style={{ position: 'relative', paddingLeft: '28px' }}>
                  {/* Linha vertical contínua */}
                  <div style={{
                    position: 'absolute',
                    left: '10px',
                    top: '12px',
                    bottom: '12px',
                    width: '2px',
                    background: 'linear-gradient(to bottom, var(--color-blue), #a5b4fc, var(--color-blue))',
                    borderRadius: '2px',
                  }} />

                  {/* Nó ORIGEM */}
                  <div style={{ position: 'relative', marginBottom: checkout.is_conexao && checkout.conexoes?.length ? '0' : '0' }}>
                    <div style={{
                      position: 'absolute',
                      left: '-28px',
                      top: '8px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-blue)',
                      border: '3px solid white',
                      boxShadow: '0 0 0 2px var(--color-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'white' }} />
                    </div>
                    <div style={{
                      padding: '10px 14px',
                      backgroundColor: '#eff6ff',
                      borderRadius: '10px',
                      border: '1px solid #bfdbfe',
                      marginBottom: checkout.is_conexao && checkout.conexoes?.length ? '12px' : '0',
                    }}>
                      <div style={{ fontSize: 'var(--font-size-11)', color: '#3b82f6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        Partida
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 'var(--font-size-16)', color: 'var(--color-secondary)', textTransform: 'capitalize', marginBottom: '2px' }}>
                        {removeHyphens(checkout.cidade_origem_slug)}
                      </div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', textTransform: 'capitalize', marginBottom: '6px' }}>
                        {removeHyphens(checkout.origem_slug)}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 'var(--font-size-12)', color: '#3b82f6', fontWeight: 500 }}>
                        <Clock size={12} />
                        {formatDate(checkout.datetime_ida)}
                      </div>
                    </div>
                  </div>

                  {/* Nós de CONEXÃO e DESTINO */}
                  {checkout.is_conexao && checkout.conexoes && checkout.conexoes.map((con, i) => {
                    const isLastConnection = i === checkout.conexoes!.length - 1;
                    
                    return (
                      <div key={con.id || i} style={{ position: 'relative', marginBottom: isLastConnection ? '0' : '12px' }}>
                        <div style={{
                          position: 'absolute',
                          left: '-28px',
                          top: '8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: isLastConnection ? '#16a34a' : '#f59e0b',
                          border: '3px solid white',
                          boxShadow: isLastConnection ? '0 0 0 2px #16a34a' : '0 0 0 2px #f59e0b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'white' }} />
                        </div>
                        <div style={{
                          padding: '10px 14px',
                          backgroundColor: isLastConnection ? '#f0fdf4' : '#fffbeb',
                          borderRadius: '10px',
                          border: isLastConnection ? '1px solid #bbf7d0' : '1px solid #fde68a',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <div style={{ 
                              fontSize: 'var(--font-size-11)', 
                              color: isLastConnection ? '#16a34a' : '#d97706', 
                              fontWeight: 700, 
                              textTransform: 'uppercase', 
                              letterSpacing: '0.5px' 
                            }}>
                              {isLastConnection ? 'Chegada' : `Conexão ${i + 1}`}
                            </div>
                            {(con.company_friendly_name || con.company_name) && (
                              <div style={{ 
                                fontSize: 'var(--font-size-11)', 
                                color: 'var(--color-darkgray)', 
                                backgroundColor: 'white', 
                                padding: '2px 8px', 
                                borderRadius: '8px', 
                                border: isLastConnection ? '1px solid #bbf7d0' : '1px solid #fde68a' 
                              }}>
                                {con.company_friendly_name || con.company_name}
                              </div>
                            )}
                          </div>
                          <div style={{ fontWeight: 700, fontSize: isLastConnection ? 'var(--font-size-16)' : 'var(--font-size-15)', color: 'var(--color-secondary)', textTransform: 'capitalize', marginBottom: '2px' }}>
                            {removeHyphens(con.cidade_destino_slug)}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', textTransform: 'capitalize', marginBottom: isLastConnection ? '6px' : '8px' }}>
                            {removeHyphens(con.destino_slug)}
                          </div>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {con.datetime_ida && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-12)', color: isLastConnection ? '#16a34a' : '#92400e' }}>
                                <Clock size={11} />
                                <span>Partida: <strong>{formatDate(con.datetime_ida)}</strong></span>
                              </div>
                            )}
                            {con.chegada_ida && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--font-size-12)', color: isLastConnection ? '#16a34a' : '#92400e', fontWeight: isLastConnection ? 500 : 'normal' }}>
                                <Clock size={11} />
                                <span>{isLastConnection ? '' : 'Chegada: '}<strong>{formatDate(con.chegada_ida)}</strong></span>
                              </div>
                            )}
                            {con.tipo_assento && !isLastConnection && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                                Assento: <strong>{con.tipo_assento}</strong>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Nó DESTINO (apenas se NÃO houver conexões) */}
                  {!checkout.is_conexao && (
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        left: '-28px',
                        top: '8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#16a34a',
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px #16a34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'white' }} />
                      </div>
                      <div style={{
                        padding: '10px 14px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '10px',
                        border: '1px solid #bbf7d0',
                      }}>
                        <div style={{ fontSize: 'var(--font-size-11)', color: '#16a34a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                          Chegada
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-16)', color: 'var(--color-secondary)', textTransform: 'capitalize', marginBottom: '2px' }}>
                          {removeHyphens(checkout.cidade_destino_slug)}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', textTransform: 'capitalize', marginBottom: '6px' }}>
                          {removeHyphens(checkout.destino_slug)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 'var(--font-size-12)', color: '#16a34a', fontWeight: 500 }}>
                          <Clock size={12} />
                          {formatDate(checkout.chegada_ida)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dados de emissão (quando completado) */}
            {checkout.status === 'completed' && (checkout.vehicle_plate || checkout.reserved_code_company || checkout.seat_number) && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)' }}>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginBottom: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Dados de Emissão
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  {checkout.vehicle_plate && (
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Placa do Veículo</div>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{checkout.vehicle_plate}</div>
                    </div>
                  )}
                  {checkout.reserved_code_company && (
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Código de Reserva</div>
                      <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{checkout.reserved_code_company}</div>
                    </div>
                  )}
                  {checkout.seat_number && (
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Número do Assento</div>
                      <div style={{ fontWeight: 600 }}>{checkout.seat_number}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)', marginTop: '16px' }}>
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Valor Base</div>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-16)' }}>{formatCurrency(checkout.max_split_value)}</div>
              </div>
              {checkout.max_split_value_markup && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Valor com Markup</div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-16)', color: 'var(--color-green)' }}>
                    {formatCurrency(checkout.max_split_value_markup)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Informações da Viagem de Volta */}
          {checkout.volta_trip_id && (
            <div className="card mb-3">
              <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bus size={20} color="var(--color-orange)" />
                Viagem de Volta
              </h3>
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr auto 1fr', 
                gap: '24px', 
                alignItems: 'center', 
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px'
              }}>
                <div>
                  <div style={{ 
                    fontSize: 'var(--font-size-12)', 
                    color: 'var(--color-darkgray)', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}>
                    Origem
                  </div>
                  <div style={{ 
                    fontWeight: 700, 
                    fontSize: '20px', 
                    marginBottom: '4px',
                    color: 'var(--color-secondary)',
                    textTransform: 'capitalize'
                  }}>
                    {removeHyphens(checkout.volta_cidade_origin_slug)}
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-darkgray)',
                    marginBottom: '12px',
                    textTransform: 'capitalize'
                  }}>
                    {removeHyphens(checkout.volta_origem_slug)}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-blue)',
                    fontWeight: 500
                  }}>
                    <Clock size={14} />
                    {formatDate(checkout.volta_datetime_ida)}
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-orange)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Bus size={20} color="white" />
                  </div>
                  <div style={{
                    width: '60px',
                    height: '2px',
                    backgroundColor: 'var(--color-gray)',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      right: '-4px',
                      top: '-3px',
                      width: '8px',
                      height: '8px',
                      borderTop: '2px solid var(--color-gray)',
                      borderRight: '2px solid var(--color-gray)',
                      transform: 'rotate(45deg)'
                    }} />
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: 'var(--font-size-12)', 
                    color: 'var(--color-darkgray)', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}>
                    Destino
                  </div>
                  <div style={{ 
                    fontWeight: 700, 
                    fontSize: '20px', 
                    marginBottom: '4px',
                    color: 'var(--color-secondary)',
                    textTransform: 'capitalize'
                  }}>
                    {removeHyphens(checkout.volta_cidade_destino_slug)}
                  </div>
                  <div style={{ 
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-darkgray)',
                    marginBottom: '12px',
                    textTransform: 'capitalize'
                  }}>
                    {removeHyphens(checkout.volta_destino_slug)}
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '6px',
                    fontSize: 'var(--font-size-14)', 
                    color: 'var(--color-blue)',
                    fontWeight: 500
                  }}>
                    <Clock size={14} />
                    {formatDate(checkout.volta_chegada_ida)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)' }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Empresa</div>
                  <div style={{ fontWeight: 500 }}>{checkout.volta_company_name}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Tipo de Assento</div>
                  <div style={{ fontWeight: 500 }}>{checkout.volta_tipo_assento}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Tipo de Veículo</div>
                  <div style={{ fontWeight: 500 }}>{checkout.volta_tipo_veiculo}</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-gray)', marginTop: '16px' }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Valor Base</div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--font-size-16)' }}>{formatCurrency(checkout.volta_max_split_value)}</div>
                </div>
                {checkout.volta_max_split_value_markup && (
                  <div>
                    <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Valor com Markup</div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-16)', color: 'var(--color-green)' }}>
                      {formatCurrency(checkout.volta_max_split_value_markup)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Passageiros */}
          <div className="card">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} />
              Passageiros
            </h3>
            
            {checkout.passengers && checkout.passengers.length > 0 ? (
              checkout.passengers.map((passenger, index) => (
                <div key={passenger.id} style={{ 
                  padding: '16px', 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px',
                  marginBottom: index < checkout.passengers!.length - 1 ? '12px' : '0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-14)' }}>Passageiro {index + 1}</span>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                      onClick={() => openPassengerEdit(passenger)}
                    >
                      <Edit2 size={12} /> Editar
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Nome</div>
                      <div style={{ fontWeight: 500 }}>{passenger.name}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Data de Nascimento</div>
                      <div style={{ fontWeight: 500 }}>{new Date(passenger.birth_date).toLocaleDateString('pt-BR')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>CPF</div>
                      <div style={{ fontWeight: 500 }}>{passenger.cpf}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>RG</div>
                      <div style={{ fontWeight: 500 }}>{passenger.rg}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Telefone</div>
                      <div style={{ fontWeight: 500 }}>{passenger.phone_number}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Nacionalidade</div>
                      <div style={{ fontWeight: 500 }}>{passenger.nationality}</div>
                    </div>
                    {(passenger as any).seat_number && (
                      <div>
                        <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Assento Ida</div>
                        <div style={{ fontWeight: 500 }}>{(passenger as any).seat_number}</div>
                      </div>
                    )}
                    {(passenger as any).volta_seat_number && (
                      <div>
                        <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Assento Volta</div>
                        <div style={{ fontWeight: 500 }}>{(passenger as any).volta_seat_number}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: 'var(--color-darkgray)' }}>Nenhum passageiro cadastrado</p>
            )}
          </div>

          {/* Modal de edição de passageiro */}
          {editingPassenger && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div className="card" style={{ width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto', padding: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0 }}>Editar Passageiro — {editingPassenger.name}</h3>
                  <button onClick={() => setEditingPassenger(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>×</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { label: 'Nome completo', value: paxName, setter: setPaxName },
                    { label: 'Data de nascimento (YYYY-MM-DD)', value: paxBirthDate, setter: setPaxBirthDate },
                    { label: 'RG', value: paxRg, setter: setPaxRg },
                    { label: 'CPF', value: paxCpf, setter: setPaxCpf },
                    { label: 'Telefone', value: paxPhone, setter: setPaxPhone },
                    { label: 'Assento Ida', value: paxSeat, setter: setPaxSeat },
                    { label: 'Assento Volta', value: paxVoltaSeat, setter: setPaxVoltaSeat },
                  ].map(({ label, value, setter }) => (
                    <div key={label}>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#555' }}>{label}</label>
                      <input className="form-control" style={{ fontSize: '13px' }} value={value} onChange={e => setter(e.target.value)} />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#dc2626' }}>Motivo da alteração *</label>
                  <input className="form-control" placeholder="Ex: Erro de digitação na data de nascimento" value={passengerReason} onChange={e => setPassengerReason(e.target.value)} />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setEditingPassenger(null)} disabled={savingPassenger}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleSavePassenger} disabled={savingPassenger || !passengerReason.trim()}>
                    {savingPassenger ? <><Loader2 size={14} className="spin" /> Salvando...</> : <><Save size={14} /> Salvar</>}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          {/* Atualização de Status */}
          <div className="card mb-3">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px' }}>
              Atualizar Status
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: 'var(--font-size-14)', fontWeight: 500 }}>
                Novo Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="form-select"
                disabled={updating}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleStatusUpdate}
              className="btn btn-primary"
              style={{ width: '100%' }}
              disabled={updating || newStatus === checkout.status}
            >
              {updating ? (
                <>
                  <Loader2 size={16} className="spin" />
                  Atualizando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Atualizar Status
                </>
              )}
            </button>
          </div>

          {/* Atualização de Status de Pagamento */}
          <div className="card mb-3">
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={18} />
              Atualizar Status do Pagamento
            </h3>
            
            <div className="form-group mb-3">
              <label htmlFor="payment-status-select" style={{ marginBottom: '8px', fontWeight: 500 }}>
                Novo Status de Pagamento
              </label>
              <select
                id="payment-status-select"
                value={checkout.paymentStatus}
                onChange={(e) => handlePaymentStatusUpdate(e.target.value as any)}
                className="form-select"
                disabled={updating}
              >
                <option value="processando">Processando</option>
                <option value="aprovado">Aprovado</option>
                <option value="reprovado">Reprovado</option>
                <option value="reembolsado">Reembolsado</option>
              </select>
            </div>

            {checkout.paymentStatus === 'reprovado' && paymentDetails.length > 0 && (() => {
              const rejected = paymentDetails.find(p => p.status === 'rejected' || p.status === 'cancelled');
              const statusDetail = rejected?.webhookData?.status_detail || rejected?.mercadoPagoResponse?.status_detail;
              if (!statusDetail) return null;
              const messages: Record<string, string> = {
                cc_rejected_bad_filled_card_number: 'Número de cartão inválido',
                cc_rejected_bad_filled_date: 'Data de vencimento inválida',
                cc_rejected_bad_filled_other: 'Verifique os dados do cartão',
                cc_rejected_bad_filled_security_code: 'Código de segurança inválido',
                cc_rejected_blacklist: 'Cartão não autorizado',
                cc_rejected_call_for_authorize: 'Entre em contato com seu banco',
                cc_rejected_card_disabled: 'Cartão desabilitado',
                cc_rejected_card_error: 'Erro no cartão',
                cc_rejected_duplicated_payment: 'Pagamento duplicado',
                cc_rejected_high_risk: 'Pagamento recusado por segurança',
                cc_rejected_insufficient_amount: 'Saldo insuficiente',
                cc_rejected_invalid_installments: 'Parcelas inválidas',
                cc_rejected_max_attempts: 'Excesso de tentativas',
                cc_rejected_other_reason: 'Cartão recusado',
                FUND: 'Saldo insuficiente',
                SECU: 'Código de segurança inválido',
                EXPI: 'Data de vencimento inválida',
                FORM: 'Erro no formulário',
                CALL: 'Recusado - ligar para autorizar',
                OTHE: 'Recusado por erro geral',
              };
              return (
                <div style={{ 
                  marginBottom: '16px', padding: '10px 12px', backgroundColor: '#fef2f2', 
                  border: '1px solid #fecaca', borderRadius: '6px', fontSize: 'var(--font-size-12)' 
                }}>
                  <strong style={{ color: '#dc2626' }}>Motivo da reprovação:</strong>{' '}
                  <span style={{ color: '#991b1b' }}>{messages[statusDetail] || statusDetail}</span>
                </div>
              );
            })()}
            
            <div style={{ 
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: 'var(--br-5)',
              fontSize: 'var(--font-size-12)',
              color: 'var(--color-darkgray)',
              marginBottom: '16px'
            }}>
              <strong>Regras de Validação:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '16px' }}>
                <li>Só é possível mudar de "Aguardando Pagamento" para "Emissão Iniciada" se o pagamento estiver "Aprovado"</li>
                <li>Só é possível cancelar uma emissão se o pagamento foi "Reembolsado" primeiro</li>
              </ul>
            </div>
          </div>

          {/* Editar Campos da Emissão */}
          <div className="card mb-3">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Edit2 size={18} color="#7c3aed" />
              Editar Campos
            </h3>
            <p style={{ fontSize: 'var(--font-size-13)', color: 'var(--color-darkgray)', marginBottom: '12px', marginTop: 0 }}>
              Altere datas, preço, código de reserva ou parceiro de emissão sem mudar o status.
            </p>
            {!showFieldsPanel ? (
              <button className="btn btn-secondary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }} onClick={openFieldsPanel}>
                <Edit2 size={14} /> Abrir Editor de Campos
              </button>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  {[
                    { label: 'Embarque Ida', value: fieldDatetimeIda, setter: setFieldDatetimeIda },
                    { label: 'Chegada Ida', value: fieldChegadaIda, setter: setFieldChegadaIda },
                    ...(checkout.volta_trip_id ? [
                      { label: 'Embarque Volta', value: fieldVoltaDatetimeIda, setter: setFieldVoltaDatetimeIda },
                      { label: 'Chegada Volta', value: fieldVoltaChegadaIda, setter: setFieldVoltaChegadaIda },
                    ] : []),
                    { label: 'Cód. Reserva Ida', value: fieldReservedCode, setter: setFieldReservedCode },
                    ...(checkout.volta_trip_id ? [
                      { label: 'Cód. Reserva Volta', value: fieldVoltaReservedCode, setter: setFieldVoltaReservedCode },
                    ] : []),
                    { label: 'Preço Base', value: fieldBaseprice, setter: setFieldBaseprice },
                    { label: 'Preço c/ Markup', value: fieldBasepriceMk, setter: setFieldBasepriceMk },
                  ].map(({ label, value, setter }) => (
                    <div key={label}>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#555' }}>{label}</label>
                      <input className="form-control" style={{ fontSize: '12px', padding: '6px 8px' }} value={value} onChange={e => setter(e.target.value)} disabled={savingFields} />
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#555' }}>Parceiro Ida</label>
                  <select className="form-select" style={{ fontSize: '12px', padding: '6px 8px' }} value={fieldEmissionPartner} onChange={e => setFieldEmissionPartner(e.target.value as BusEmissionPartner | '')} disabled={savingFields}>
                    <option value="">— Selecione —</option>
                    {BUS_EMISSION_PARTNERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                {checkout.volta_trip_id && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#555' }}>Parceiro Volta</label>
                    <select className="form-select" style={{ fontSize: '12px', padding: '6px 8px' }} value={fieldVoltaEmissionPartner} onChange={e => setFieldVoltaEmissionPartner(e.target.value as BusEmissionPartner | '')} disabled={savingFields}>
                      <option value="">— Selecione —</option>
                      {BUS_EMISSION_PARTNERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#dc2626' }}>Motivo da alteração *</label>
                  <input className="form-control" style={{ fontSize: '12px' }} placeholder="Ex: Parceiro alterou horário" value={fieldsReason} onChange={e => setFieldsReason(e.target.value)} disabled={savingFields} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" style={{ flex: 1, fontSize: '13px' }} onClick={() => setShowFieldsPanel(false)} disabled={savingFields}>Cancelar</button>
                  <button className="btn btn-primary" style={{ flex: 1, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }} onClick={handleSaveFields} disabled={savingFields || !fieldsReason.trim()}>
                    {savingFields ? <><Loader2 size={13} className="spin" /> Salvando...</> : <><Save size={13} /> Salvar</>}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Reenviar Notificações */}
          <div className="card mb-3">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={18} color="#0284c7" />
              Reenviar Notificações
            </h3>
            <p style={{ fontSize: 'var(--font-size-13)', color: 'var(--color-darkgray)', marginBottom: '12px', marginTop: 0 }}>
              Reenvie email, WhatsApp ou SMS manualmente para o cliente.
            </p>
            {!showNotifyPanel ? (
              <button className="btn btn-secondary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }} onClick={() => { setNotifyTemplate(checkout.status); setShowNotifyPanel(true); }}>
                <RefreshCw size={14} /> Reenviar Notificação
              </button>
            ) : (
              <div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#555' }}>Canais</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    {(['email', 'whatsapp', 'sms'] as const).map(ch => (
                      <label key={ch} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!notifyChannels[ch]} onChange={e => setNotifyChannels(prev => ({ ...prev, [ch]: e.target.checked }))} disabled={sendingNotify} />
                        {ch.charAt(0).toUpperCase() + ch.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#555' }}>Template</label>
                  <select className="form-select" style={{ fontSize: '12px', padding: '6px 8px' }} value={notifyTemplate} onChange={e => setNotifyTemplate(e.target.value)} disabled={sendingNotify}>
                    <option value="emission started">Emissão Iniciada</option>
                    <option value="awaiting payment">Aguardando Pagamento</option>
                    <option value="completed">Completado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '3px', color: '#555' }}>Motivo (opcional)</label>
                  <input className="form-control" style={{ fontSize: '12px' }} placeholder="Ex: Cliente não recebeu o email" value={notifyReason} onChange={e => setNotifyReason(e.target.value)} disabled={sendingNotify} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" style={{ flex: 1, fontSize: '13px' }} onClick={() => setShowNotifyPanel(false)} disabled={sendingNotify}>Cancelar</button>
                  <button className="btn btn-primary" style={{ flex: 1, fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }} onClick={handleResendNotification} disabled={sendingNotify}>
                    {sendingNotify ? <><Loader2 size={13} className="spin" /> Enviando...</> : <><Send size={13} /> Enviar</>}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Informações do Cliente */}
          <div className="card mb-3">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px' }}>
              Informações do Cliente
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} color="var(--color-darkgray)" />
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Nome</div>
                  <div style={{ fontWeight: 500 }}>{checkout.name}</div>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mail size={16} color="var(--color-darkgray)" />
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Email</div>
                  <div style={{ fontWeight: 500 }}>{checkout.email}</div>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Phone size={16} color="var(--color-darkgray)" />
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Telefone</div>
                  <div style={{ fontWeight: 500 }}>{checkout.fone_payment}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo Financeiro */}
          <div className="card">
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px' }}>
              Resumo Financeiro
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--color-darkgray)' }}>Ida</span>
                <span style={{ fontWeight: 500 }}>{formatCurrency(checkout.max_split_value_markup || checkout.max_split_value)}</span>
              </div>
              
              {checkout.volta_trip_id && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-darkgray)' }}>Volta</span>
                  <span style={{ fontWeight: 500 }}>{formatCurrency(checkout.volta_max_split_value_markup || checkout.volta_max_split_value)}</span>
                </div>
              )}
              
              {checkout.baseprice && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--color-darkgray)' }}>Preço Base</span>
                  <span style={{ fontWeight: 500 }}>{formatCurrency(checkout.baseprice)}</span>
                </div>
              )}
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                paddingTop: '12px', 
                borderTop: '2px solid var(--color-gray)',
                marginTop: '8px'
              }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-16)' }}>Total</span>
                <span style={{ fontWeight: 600, fontSize: 'var(--font-size-18)', color: 'var(--color-green)' }}>
                  {formatCurrency(checkout.total_value.toString())}
                </span>
              </div>

              {/* Detalhes do Pagamento (Mercado Pago) */}
              {paymentDetails.length > 0 && (() => {
                const payment = paymentDetails[0];
                const methodLabels: Record<string, string> = {
                  pix: 'PIX',
                  credit_card: 'Cartão de Crédito',
                  debit_card: 'Cartão de Débito',
                };
                return (
                  <div style={{ 
                    marginTop: '12px', paddingTop: '12px', 
                    borderTop: '1px solid var(--color-gray)' 
                  }}>
                    <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginBottom: '8px', fontWeight: 600 }}>
                      Dados do Pagamento
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: 'var(--color-darkgray)' }}>Método</span>
                      <span style={{ fontWeight: 500 }}>{methodLabels[payment.paymentMethod] || payment.paymentMethod}</span>
                    </div>
                    {payment.installments && payment.installments > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ color: 'var(--color-darkgray)' }}>Parcelas</span>
                        <span style={{ fontWeight: 500 }}>{payment.installments}x</span>
                      </div>
                    )}
                    {payment.cardLastFourDigits && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ color: 'var(--color-darkgray)' }}>Cartão</span>
                        <span style={{ fontWeight: 500 }}>
                          {payment.cardBrand ? `${payment.cardBrand} ` : ''}•••• {payment.cardLastFourDigits}
                        </span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--color-darkgray)' }}>Status</span>
                      <span style={{ 
                        fontWeight: 500, 
                        color: payment.status === 'approved' ? '#16a34a' : 
                               payment.status === 'rejected' ? '#dc2626' : '#ea580c' 
                      }}>
                        {payment.status === 'approved' ? 'Aprovado' :
                         payment.status === 'rejected' ? 'Rejeitado' :
                         payment.status === 'cancelled' ? 'Cancelado' :
                         payment.status === 'pending' ? 'Pendente' : payment.status}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Tentativas de Pagamento — Mercado Pago */}
          {paymentDetails.length > 0 && (() => {
            const mpStatusLabel: Record<string, string> = {
              approved:     'Aprovado',
              pending:      'Pendente',
              in_process:   'Em análise',
              rejected:     'Reprovado',
              cancelled:    'Cancelado',
              refunded:     'Reembolsado',
              charged_back: 'Estornado',
            };
            const mpStatusColor: Record<string, { bg: string; border: string; text: string }> = {
              approved:     { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
              pending:      { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
              in_process:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
              rejected:     { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' },
              cancelled:    { bg: '#f3f4f6', border: '#d1d5db', text: '#6b7280' },
              refunded:     { bg: '#f5f3ff', border: '#ddd6fe', text: '#7c3aed' },
              charged_back: { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
            };
            const mpStatusDetailMessages: Record<string, string> = {
              accredited:                          'Pagamento acreditado com sucesso',
              pending_contingency:                 'Pagamento pendente — aguardando processamento',
              pending_review_manual:               'Em revisão manual pelo Mercado Pago',
              cc_rejected_bad_filled_card_number:  'Número do cartão inválido',
              cc_rejected_bad_filled_date:         'Data de validade inválida',
              cc_rejected_bad_filled_other:        'Dados do cartão incorretos',
              cc_rejected_bad_filled_security_code:'Código de segurança inválido',
              cc_rejected_blacklist:               'Cartão bloqueado / lista negra',
              cc_rejected_call_for_authorize:      'Banco solicitou autorização — cliente deve ligar',
              cc_rejected_card_disabled:           'Cartão desabilitado pelo banco',
              cc_rejected_card_error:              'Erro no processamento do cartão',
              cc_rejected_duplicated_payment:      'Pagamento duplicado detectado',
              cc_rejected_high_risk:               'Recusado por análise de risco (antifraude)',
              cc_rejected_insufficient_amount:     'Saldo insuficiente',
              cc_rejected_invalid_installments:    'Número de parcelas inválido',
              cc_rejected_max_attempts:            'Limite de tentativas excedido',
              cc_rejected_other_reason:            'Cartão recusado (motivo genérico)',
              refunded:                            'Pagamento estornado ao cliente',
              FUND: 'Saldo insuficiente',
              SECU: 'Código de segurança inválido',
              EXPI: 'Data de validade expirada',
              FORM: 'Erro de formulário',
              CALL: 'Ligar para o banco para autorizar',
              OTHE: 'Recusado — erro geral',
            };
            const cardBrandLabel: Record<string, string> = {
              visa: 'Visa', master: 'Mastercard', amex: 'American Express',
              elo: 'Elo', hipercard: 'Hipercard', hiper: 'Hiper',
              diners: 'Diners Club', discover: 'Discover',
            };
            const refundStatusLabel: Record<string, string> = {
              approved: 'Aprovado', pending: 'Pendente', rejected: 'Rejeitado',
            };

            return (
              <div className="card" style={{ marginTop: '16px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CreditCard size={18} />
                  Tentativas de Pagamento
                  <span style={{
                    marginLeft: '4px', fontSize: 'var(--font-size-12)', fontWeight: 400,
                    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                    borderRadius: '12px', padding: '1px 8px', color: '#6b7280',
                  }}>
                    {paymentDetails.length}
                  </span>
                </h3>

                <div className="d-flex flex-column" style={{ gap: '16px' }}>
                  {paymentDetails.map((payment, idx) => {
                    const mpData = payment.webhookData || payment.mercadoPagoResponse;
                    const statusDetail = payment.webhookData?.status_detail || payment.mercadoPagoResponse?.status_detail;
                    const mpStatus = payment.webhookData?.status || payment.mercadoPagoResponse?.status || payment.status;
                    const colors = mpStatusColor[mpStatus] || mpStatusColor['cancelled'];
                    const transactionDetails = mpData?.transaction_details;
                    const chargesDetails = mpData?.charges_details || [];
                    const additionalPayer = mpData?.additional_info?.payer;
                    const additionalAddress = additionalPayer?.address;
                    const threeDsInfo = mpData?.three_ds_info;
                    const has3ds = !!(threeDsInfo?.creq || threeDsInfo?.external_resource_url);
                    const mpFee = chargesDetails.find((c: any) => c.name === 'mercadopago_fee');
                    const statementDescriptor = payment.statement_descriptor_invoice || mpData?.statement_descriptor;
                    const moneyReleaseDate = payment.webhookData?.money_release_date || payment.mercadoPagoResponse?.money_release_date;
                    const moneyReleaseStatus = payment.webhookData?.money_release_status || payment.mercadoPagoResponse?.money_release_status;
                    const processingMode = mpData?.processing_mode;
                    const operationType = mpData?.operation_type;
                    // Refunds vêm do webhookData no ônibus
                    const refunds: any[] = mpData?.refunds || [];
                    const transactionAmountRefunded = mpData?.transaction_amount_refunded ?? 0;
                    // Cardholder da resposta do MP
                    const cardholderName = payment.mercadoPagoResponse?.card?.cardholder?.name || payment.webhookData?.card?.cardholder?.name;
                    const cardBin = (payment.mercadoPagoResponse?.card as any)?.bin || (payment.webhookData?.card as any)?.bin || (payment.mercadoPagoResponse?.card as any)?.first_six_digits || (payment.webhookData?.card as any)?.first_six_digits;
                    const cardExpYear = payment.mercadoPagoResponse?.card?.expiration_year || payment.webhookData?.card?.expiration_year;
                    const cardExpMonth = payment.mercadoPagoResponse?.card?.expiration_month || payment.webhookData?.card?.expiration_month;
                    // Payer
                    const payerEmail = mpData?.payer?.email;
                    const payerDoc = mpData?.payer?.identification;
                    // Items da viagem
                    const tripItems: any[] = mpData?.additional_info?.items || [];
                    // Código de autorização
                    const authCode = payment.mercadoPagoResponse?.authorization_code || payment.webhookData?.authorization_code;

                    return (
                      <div key={payment.id} style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        backgroundColor: colors.bg,
                        overflow: 'hidden',
                      }}>
                        {/* Cabeçalho */}
                        <div style={{
                          padding: '12px 16px',
                          borderBottom: `1px solid ${colors.border}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 'var(--font-size-12)', fontWeight: 700, color: 'var(--color-darkgray)' }}>
                              #{idx + 1}
                            </span>
                            <span style={{
                              fontSize: 'var(--font-size-12)', fontWeight: 700,
                              backgroundColor: colors.border, color: colors.text,
                              padding: '2px 10px', borderRadius: '20px',
                            }}>
                              {mpStatusLabel[mpStatus] || mpStatus}
                            </span>
                            {statusDetail && (
                              <span style={{
                                fontSize: 'var(--font-size-11)', color: colors.text,
                                backgroundColor: 'white', border: `1px solid ${colors.border}`,
                                padding: '2px 8px', borderRadius: '20px',
                              }}>
                                {mpStatusDetailMessages[statusDetail] || statusDetail}
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-dimgray)' }}>
                            {new Date(payment.createdAt).toLocaleString('pt-BR')}
                          </span>
                        </div>

                        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

                          {/* Alerta 3DS */}
                          {has3ds && (
                            <div style={{
                              padding: '10px 12px',
                              backgroundColor: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: '6px',
                              fontSize: 'var(--font-size-12)',
                              color: '#1e40af',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px',
                            }}>
                              <span style={{ fontSize: '16px', lineHeight: 1 }}>🔐</span>
                              <div>
                                <strong>Autenticação 3D Secure (3DS) solicitada</strong>
                                <div style={{ marginTop: '2px', color: '#2563eb' }}>
                                  O banco emissor do cartão exigiu autenticação adicional do portador.
                                  {threeDsInfo?.external_resource_url && (
                                    <span> URL: <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{threeDsInfo.external_resource_url}</span></span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Sem 3DS */}
                          {!has3ds && mpStatus === 'approved' && (
                            <div style={{
                              padding: '6px 12px',
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #bbf7d0',
                              borderRadius: '6px',
                              fontSize: 'var(--font-size-12)',
                              color: '#15803d',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}>
                              <span>🔓</span>
                              <span>Sem autenticação 3DS — pagamento processado diretamente</span>
                            </div>
                          )}

                          {/* IDs e referências */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>ID Mercado Pago</div>
                              <div style={{ fontSize: 'var(--font-size-12)', fontFamily: 'monospace', wordBreak: 'break-all', fontWeight: 500 }}>{payment.mercadoPagoId}</div>
                            </div>
                            {payment.externalReference && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Referência Externa</div>
                                <div style={{ fontSize: 'var(--font-size-12)', fontFamily: 'monospace', wordBreak: 'break-all' }}>{payment.externalReference}</div>
                              </div>
                            )}
                            {authCode && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Código de Autorização</div>
                                <div style={{ fontSize: 'var(--font-size-12)', fontFamily: 'monospace', fontWeight: 500 }}>{authCode}</div>
                              </div>
                            )}
                            {payment.idempotencyKey && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Idempotency Key</div>
                                <div style={{ fontSize: 'var(--font-size-11)', fontFamily: 'monospace', wordBreak: 'break-all', color: '#6b7280' }}>{payment.idempotencyKey}</div>
                              </div>
                            )}
                          </div>

                          {/* Cartão */}
                          {(payment.cardBrand || payment.cardLastFourDigits || payment.paymentTypeId) && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Cartão</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                {payment.cardBrand && (
                                  <span style={{
                                    fontSize: 'var(--font-size-13)', fontWeight: 700,
                                    backgroundColor: '#f3f4f6', border: '1px solid #d1d5db',
                                    padding: '3px 10px', borderRadius: '6px', color: '#374151',
                                  }}>
                                    {cardBrandLabel[payment.cardBrand] || payment.cardBrand.toUpperCase()}
                                  </span>
                                )}
                                {payment.paymentTypeId === 'credit_card' && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>Crédito</span>
                                )}
                                {payment.paymentTypeId === 'debit_card' && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>Débito</span>
                                )}
                                {payment.cardLastFourDigits && (
                                  <span style={{ fontSize: 'var(--font-size-14)', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '2px' }}>
                                    •••• •••• •••• {payment.cardLastFourDigits}
                                  </span>
                                )}
                                {cardBin && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#9ca3af', fontFamily: 'monospace' }}>
                                    BIN: {cardBin}
                                  </span>
                                )}
                                {(payment.installments ?? 1) > 1 && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#2563eb', fontWeight: 500 }}>
                                    {payment.installments}x de {formatCurrency(String(Number(payment.amount) / (payment.installments ?? 1)))}
                                  </span>
                                )}
                                {(payment.installments ?? 1) === 1 && (
                                  <span style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>À vista</span>
                                )}
                              </div>
                              {cardholderName && (
                                <div style={{ marginTop: '6px', fontSize: 'var(--font-size-12)', color: '#374151' }}>
                                  <span style={{ color: '#9ca3af' }}>Titular: </span>
                                  <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{cardholderName}</span>
                                </div>
                              )}
                              {(cardExpMonth || cardExpYear) && (
                                <div style={{ marginTop: '2px', fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                  Validade: {String(cardExpMonth).padStart(2, '0')}/{cardExpYear}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Pagador */}
                          {(payerEmail || payerDoc) && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Pagador (Mercado Pago)</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--font-size-12)' }}>
                                {payerEmail && (
                                  <div>
                                    <span style={{ color: '#9ca3af' }}>Email: </span>
                                    <span style={{ color: '#374151' }}>{payerEmail}</span>
                                  </div>
                                )}
                                {payerDoc?.number && (
                                  <div>
                                    <span style={{ color: '#9ca3af' }}>{payerDoc.type}: </span>
                                    <span style={{ fontFamily: 'monospace', color: '#374151' }}>{payerDoc.number}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Endereço de cobrança */}
                          {(payment.billingStreetName || payment.billingZipCode) && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Endereço de Cobrança</div>
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#374151' }}>
                                {payment.billingStreetName}{payment.billingStreetNumber ? `, ${payment.billingStreetNumber}` : ''}
                                {payment.billingZipCode && (
                                  <span style={{ marginLeft: '8px', color: '#6b7280' }}>CEP: {payment.billingZipCode}</span>
                                )}
                              </div>
                              {additionalPayer && (
                                <div style={{ marginTop: '4px', fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                  {additionalPayer.first_name} {additionalPayer.last_name}
                                  {additionalPayer.phone?.number && (
                                    <span style={{ marginLeft: '8px' }}>({additionalPayer.phone.area_code}) {additionalPayer.phone.number}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Itens da viagem */}
                          {tripItems.length > 0 && (
                            <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Itens do Pedido</div>
                              {tripItems.map((item: any, i: number) => (
                                <div key={i} style={{ fontSize: 'var(--font-size-12)', color: '#374151', display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '4px 0', borderBottom: i < tripItems.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                  <div>
                                    <div style={{ fontWeight: 500 }}>{item.title}</div>
                                    <div style={{ color: '#9ca3af' }}>{item.quantity}x • {item.description}</div>
                                  </div>
                                  <div style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    {formatCurrency(String(Number(item.unit_price) * Number(item.quantity)))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Valores financeiros */}
                          <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px' }}>
                            <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Valores</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: 'var(--font-size-13)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Valor cobrado:</span>
                                <strong>{formatCurrency(payment.amount?.toString() ?? null)}</strong>
                              </div>
                              {transactionDetails?.total_paid_amount != null && transactionDetails.total_paid_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Total pago (MP):</span>
                                  <span>{formatCurrency(String(transactionDetails.total_paid_amount))}</span>
                                </div>
                              )}
                              {mpFee && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Taxa Mercado Pago:</span>
                                  <span style={{ color: '#c2410c' }}>- {formatCurrency(String(mpFee.amounts?.original ?? Number(payment.fee) ?? 0))}</span>
                                </div>
                              )}
                              {!mpFee && payment.fee && Number(payment.fee) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Taxa Mercado Pago:</span>
                                  <span style={{ color: '#c2410c' }}>- {formatCurrency(payment.fee?.toString() ?? null)}</span>
                                </div>
                              )}
                              {transactionDetails?.net_received_amount != null && transactionDetails.net_received_amount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #e5e7eb', paddingTop: '5px', marginTop: '2px' }}>
                                  <span style={{ color: '#6b7280' }}>Valor líquido recebido:</span>
                                  <span style={{ color: '#15803d', fontWeight: 600 }}>{formatCurrency(String(transactionDetails.net_received_amount))}</span>
                                </div>
                              )}
                              {!transactionDetails?.net_received_amount && payment.netAmount && Number(payment.netAmount) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #e5e7eb', paddingTop: '5px', marginTop: '2px' }}>
                                  <span style={{ color: '#6b7280' }}>Valor líquido recebido:</span>
                                  <span style={{ color: '#15803d', fontWeight: 600 }}>{formatCurrency(payment.netAmount?.toString() ?? null)}</span>
                                </div>
                              )}
                              {transactionAmountRefunded > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Total reembolsado:</span>
                                  <span style={{ color: '#7c3aed', fontWeight: 500 }}>{formatCurrency(String(transactionAmountRefunded))}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Reembolsos */}
                          {refunds.length > 0 && (
                            <div style={{ backgroundColor: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: '6px', padding: '10px 14px' }}>
                              <div style={{ fontSize: 'var(--font-size-11)', color: '#7c3aed', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                                Reembolsos ({refunds.length})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {refunds.map((refund: any, ri: number) => (
                                  <div key={refund.id || ri} style={{
                                    backgroundColor: 'white',
                                    border: '1px solid #ddd6fe',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: 'var(--font-size-12)',
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                      <span style={{ fontFamily: 'monospace', color: '#6b7280', fontSize: 'var(--font-size-11)' }}>#{refund.id}</span>
                                      <span style={{
                                        fontSize: 'var(--font-size-11)', fontWeight: 700,
                                        color: refund.status === 'approved' ? '#15803d' : refund.status === 'pending' ? '#92400e' : '#dc2626',
                                        backgroundColor: refund.status === 'approved' ? '#f0fdf4' : refund.status === 'pending' ? '#fffbeb' : '#fef2f2',
                                        padding: '1px 8px', borderRadius: '20px',
                                        border: `1px solid ${refund.status === 'approved' ? '#bbf7d0' : refund.status === 'pending' ? '#fde68a' : '#fecaca'}`,
                                      }}>
                                        {refundStatusLabel[refund.status] || refund.status}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                                      <div>
                                        <span style={{ color: '#9ca3af' }}>Valor devolvido: </span>
                                        <strong style={{ color: '#7c3aed' }}>{formatCurrency(String(refund.amount_refunded_to_payer ?? refund.amount))}</strong>
                                      </div>
                                      {refund.date_created && (
                                        <div style={{ color: '#9ca3af' }}>
                                          {new Date(refund.date_created).toLocaleString('pt-BR')}
                                        </div>
                                      )}
                                    </div>
                                    {refund.source?.name && (
                                      <div style={{ marginTop: '2px', color: '#6b7280' }}>
                                        Solicitado por: {refund.source.name}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Fatura e repasse */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {statementDescriptor && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>
                                  Aparece na fatura como
                                </div>
                                <div style={{ fontSize: 'var(--font-size-13)', fontWeight: 700, fontFamily: 'monospace', color: '#111827', letterSpacing: '1px' }}>
                                  {statementDescriptor.trim()}
                                </div>
                              </div>
                            )}
                            {(moneyReleaseDate || moneyReleaseStatus) && (
                              <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 12px' }}>
                                <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Repasse</div>
                                {moneyReleaseStatus && (
                                  <div style={{ fontSize: 'var(--font-size-12)', color: moneyReleaseStatus === 'released' ? '#15803d' : '#92400e', fontWeight: 500 }}>
                                    {moneyReleaseStatus === 'released' ? '✓ Liberado' : moneyReleaseStatus === 'pending' ? '⏳ Pendente' : moneyReleaseStatus}
                                  </div>
                                )}
                                {moneyReleaseDate && (
                                  <div style={{ fontSize: 'var(--font-size-12)', color: '#6b7280', marginTop: '2px' }}>
                                    {new Date(moneyReleaseDate).toLocaleDateString('pt-BR')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Modo de processamento e ambiente */}
                          {(processingMode || operationType || mpData?.live_mode != null) && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {processingMode && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f3f4f6',
                                  border: '1px solid #d1d5db', borderRadius: '4px',
                                  padding: '3px 8px', color: '#6b7280',
                                }}>
                                  Modo: {processingMode}
                                </span>
                              )}
                              {operationType && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f3f4f6',
                                  border: '1px solid #d1d5db', borderRadius: '4px',
                                  padding: '3px 8px', color: '#6b7280',
                                }}>
                                  Operação: {operationType}
                                </span>
                              )}
                              {mpData?.live_mode === true && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#f0fdf4',
                                  border: '1px solid #bbf7d0', borderRadius: '4px',
                                  padding: '3px 8px', color: '#15803d', fontWeight: 600,
                                }}>
                                  PRODUÇÃO
                                </span>
                              )}
                              {mpData?.live_mode === false && (
                                <span style={{
                                  fontSize: 'var(--font-size-11)', backgroundColor: '#fef3c7',
                                  border: '1px solid #fde68a', borderRadius: '4px',
                                  padding: '3px 8px', color: '#92400e', fontWeight: 600,
                                }}>
                                  SANDBOX
                                </span>
                              )}
                            </div>
                          )}

                          {/* Datas */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                            {mpData?.date_created && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                <span style={{ fontWeight: 500, color: '#374151' }}>Criado: </span>
                                {new Date(mpData.date_created).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {mpData?.date_approved && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#15803d' }}>
                                <span style={{ fontWeight: 500 }}>Aprovado: </span>
                                {new Date(mpData.date_approved).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {payment.paidAt && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#15803d' }}>
                                <span style={{ fontWeight: 500 }}>Pago em: </span>
                                {new Date(payment.paidAt).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {mpData?.date_last_updated && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#6b7280' }}>
                                <span style={{ fontWeight: 500, color: '#374151' }}>Atualizado: </span>
                                {new Date(mpData.date_last_updated).toLocaleString('pt-BR')}
                              </div>
                            )}
                            {payment.expiresAt && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#c2410c' }}>
                                <span style={{ fontWeight: 500 }}>Expira: </span>
                                {new Date(payment.expiresAt).toLocaleString('pt-BR')}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Informações Adicionais */}
          <div className="card" style={{ marginTop: '16px' }}>
            <h3 style={{ fontSize: 'var(--font-size-18)', fontWeight: 600, marginBottom: '16px' }}>
              Informações Adicionais
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>ID da Viagem (Ida)</div>
                <div style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-14)' }}>{checkout.trip_id}</div>
              </div>
              
              {checkout.volta_trip_id && (
                <div>
                  <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>ID da Viagem (Volta)</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-14)' }}>{checkout.volta_trip_id}</div>
                </div>
              )}
              
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Criado em</div>
                <div style={{ fontSize: 'var(--font-size-14)' }}>{formatDate(checkout.created_at)}</div>
              </div>
              
              <div>
                <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>Atualizado em</div>
                <div style={{ fontSize: 'var(--font-size-14)' }}>{formatDate(checkout.updated_at)}</div>
              </div>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="card" style={{ marginTop: '16px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <MessageCircle size={18} color="#25D366" />
              WhatsApp
            </h3>

            {loadingWhatsapp ? (
              <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                <Loader2 size={16} className="animate-spin" />
                <span style={{ marginLeft: '8px', fontSize: 'var(--font-size-14)' }}>Carregando...</span>
              </div>
            ) : whatsappEvents?.messages?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {whatsappEvents.messages.map((msg: any, idx: number) => {
                  const statusEvts = msg.events?.filter((e: any) => e.type === 'status') ?? [];
                  const replyEvts = msg.events?.filter((e: any) => e.type === 'message') ?? [];
                  const lastStatusEvt = statusEvts.length > 0 ? statusEvts[statusEvts.length - 1] : null;
                  const lastStatus = lastStatusEvt?.status || 'sent';

                  const statusCfg: Record<string, { color: string; bg: string; border: string; label: string; icon: string }> = {
                    sent:      { color: '#6c757d', bg: '#f8f9fa', border: '#dee2e6', label: 'Enviada',   icon: '📤' },
                    delivered: { color: '#28a745', bg: '#e8f5e9', border: '#a3d9a5', label: 'Entregue',  icon: '✅' },
                    read:      { color: '#007bff', bg: '#e3f2fd', border: '#90caf9', label: 'Lida',      icon: '👁️' },
                    failed:    { color: '#dc3545', bg: '#fdecea', border: '#f5c6cb', label: 'Falhou',    icon: '❌' },
                  };
                  const sc = statusCfg[lastStatus] || statusCfg.sent;

                  const errorDescriptions: Record<number, string> = {
                    131026: 'Número fora da janela de 24h ou não tem WhatsApp',
                    131047: 'Re-engagement message — fora da janela',
                    131000: 'Erro genérico no envio',
                    131005: 'Permissão negada',
                    131008: 'Parâmetro obrigatório ausente',
                    131009: 'Parâmetro inválido',
                    131021: 'Número inválido ou não registrado',
                    131051: 'Tipo de mensagem não suportado',
                    131052: 'Mídia inválida',
                    131053: 'Erro no upload da mídia',
                    130429: 'Limite de taxa atingido',
                    131042: 'Conta de negócios não elegível',
                  };

                  return (
                    <div key={idx} style={{
                      border: `1px solid ${sc.border}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: sc.bg,
                    }}>
                      {/* Cabeçalho da mensagem */}
                      <div style={{
                        padding: '10px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px',
                        borderBottom: `1px solid ${sc.border}`,
                        backgroundColor: 'rgba(255,255,255,0.6)',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-13)',
                            color: '#1a202c',
                            fontFamily: 'monospace',
                            background: '#f1f5f9',
                            padding: '2px 7px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                          }}>
                            {msg.template}
                          </span>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {msg.phoneNumber}
                          </span>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {new Date(msg.sentAt).toLocaleString('pt-BR')}
                          </span>
                        </div>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: 'var(--font-size-12)',
                          fontWeight: 600,
                          color: sc.color,
                          backgroundColor: 'white',
                          border: `1px solid ${sc.border}`,
                        }}>
                          {sc.icon} {sc.label}
                        </span>
                      </div>

                      {/* Detalhes do erro (se falhou) */}
                      {lastStatus === 'failed' && lastStatusEvt && (
                        <div style={{
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          borderBottom: replyEvts.length > 0 ? `1px solid ${sc.border}` : undefined,
                        }}>
                          <span style={{ fontSize: '14px', marginTop: '1px' }}>⚠️</span>
                          <div>
                            <div style={{ fontSize: 'var(--font-size-12)', fontWeight: 600, color: '#dc3545' }}>
                              {lastStatusEvt.errorTitle || 'Erro no envio'}
                              {lastStatusEvt.errorCode && (
                                <span style={{ fontWeight: 400, marginLeft: '6px', color: '#6c757d' }}>
                                  (código {lastStatusEvt.errorCode})
                                </span>
                              )}
                            </div>
                            {lastStatusEvt.errorCode && errorDescriptions[lastStatusEvt.errorCode] && (
                              <div style={{ fontSize: 'var(--font-size-12)', color: '#6c757d', marginTop: '2px' }}>
                                {errorDescriptions[lastStatusEvt.errorCode]}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Timeline de status */}
                      {statusEvts.length > 1 && (
                        <div style={{
                          padding: '8px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap',
                          borderBottom: replyEvts.length > 0 ? `1px solid ${sc.border}` : undefined,
                          backgroundColor: 'rgba(255,255,255,0.4)',
                        }}>
                          <span style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)', marginRight: '4px' }}>
                            Histórico:
                          </span>
                          {statusEvts.map((evt: any, i: number) => {
                            const c = statusCfg[evt.status] || statusCfg.sent;
                            return (
                              <span key={i} style={{
                                padding: '2px 7px',
                                borderRadius: '10px',
                                fontSize: 'var(--font-size-11)',
                                fontWeight: 500,
                                color: c.color,
                                backgroundColor: 'white',
                                border: `1px solid ${c.border}`,
                              }}
                                title={new Date(Number(evt.timestamp) * 1000).toLocaleString('pt-BR')}
                              >
                                {c.icon} {c.label}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Respostas do cliente */}
                      {replyEvts.length > 0 && (
                        <div style={{ padding: '10px 14px' }}>
                          <div style={{
                            fontSize: 'var(--font-size-12)',
                            fontWeight: 600,
                            color: '#6f42c1',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            <span>💬</span> {replyEvts.length === 1 ? 'Resposta do cliente' : `${replyEvts.length} respostas do cliente`}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {replyEvts.map((evt: any, i: number) => {
                              const replyText = evt.payload?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.text?.body;
                              const replyTs = evt.timestamp
                                ? new Date(Number(evt.timestamp) * 1000).toLocaleString('pt-BR')
                                : evt.createdAt
                                  ? new Date(evt.createdAt).toLocaleString('pt-BR')
                                  : null;
                              return (
                                <div key={i} style={{
                                  background: 'white',
                                  border: '1px solid #e9d8fd',
                                  borderRadius: '6px',
                                  padding: '8px 12px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px',
                                }}>
                                  {replyText ? (
                                    <span style={{ fontSize: 'var(--font-size-13)', color: '#1a202c' }}>
                                      "{replyText}"
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                                      Mensagem não textual (áudio, imagem, etc.)
                                    </span>
                                  )}
                                  {replyTs && (
                                    <span style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-darkgray)' }}>
                                      {replyTs}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '24px', color: 'var(--color-darkgray)' }}>
                <MessageCircle size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  Nenhuma mensagem WhatsApp enviada
                </p>
              </div>
            )}
          </div>

          {/* Cartão de Embarque */}
          {checkout.status === 'completed' && (
            <div className="card" style={{ marginTop: '16px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} color="#059669" />
                Cartão de Embarque
              </h3>

              {/* Upload de novo boarding pass */}
              <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const form = e.target as HTMLFormElement;
                    const formData = new FormData(form);
                    if (!formData.get('file') || !(formData.get('file') as File).size) return;
                    setUploadingBp(true);
                    try {
                      formData.append('uploadedBy', user?.username || 'admin');
                      await busCheckoutService.uploadBoardingPass(id!, formData);
                      await loadBoardingPasses();
                      form.reset();
                    } catch (err: any) {
                      const msg = err.response?.data?.message || 'Erro no upload';
                      setErrorMessage(msg);
                      setShowErrorAlert(true);
                    } finally {
                      setUploadingBp(false);
                    }
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
                >
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end' }}>
                    <div style={{ minWidth: '140px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Passageiro</label>
                      <select name="passengerName" required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        {checkout.passengers?.map((p) => (
                          <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ width: '80px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Assento</label>
                      <input name="seat" placeholder="42" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: '160px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>PDF do Bilhete</label>
                      <input name="file" type="file" accept="application/pdf" required style={{ fontSize: '13px' }} />
                    </div>
                    <button type="submit" disabled={uploadingBp} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}>
                      {uploadingBp ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      Upload
                    </button>
                  </div>
                </form>
              </div>

              {/* Lista de boarding passes */}
              {loadingBoardingPasses ? (
                <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                  <Loader2 size={16} className="animate-spin" />
                  <span style={{ marginLeft: '8px', fontSize: 'var(--font-size-14)' }}>Carregando...</span>
                </div>
              ) : boardingPasses.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {boardingPasses.map((bp) => (
                    <div key={bp.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '8px',
                      border: '1px solid #bbf7d0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FileText size={20} color="#059669" />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px' }}>{bp.passengerName}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {bp.seat && `Poltrona ${bp.seat} • `}
                            {bp.originalFilename}
                            {bp.sentAt && (
                              <span style={{ color: '#059669', marginLeft: '8px' }}>
                                ✓ Enviado {new Date(bp.sentAt).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {bp.url && (
                          <a
                            href={bp.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            <FileDown size={14} />
                            Ver
                          </a>
                        )}
                        <button
                          onClick={() => handleSendBoardingPass(bp.id)}
                          disabled={sendingBp === bp.id}
                          className="btn btn-primary"
                          style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                          title="Enviar via WhatsApp"
                        >
                          {sendingBp === bp.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          WhatsApp
                        </button>
                        <button
                          onClick={() => handleDeleteBoardingPass(bp.id)}
                          style={{ padding: '6px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', background: 'none', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer' }}
                          title="Remover"
                        >
                          <Trash2 size={14} color="#dc2626" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center" style={{ padding: '24px', color: 'var(--color-darkgray)' }}>
                  <FileText size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                    Nenhum cartão de embarque enviado ainda
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Histórico de Alterações */}
          <div className="card" style={{ marginTop: '16px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 'var(--font-size-18)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <History size={18} />
              Histórico de Alterações
            </h3>
            
            {loadingHistory ? (
              <div className="d-flex align-center justify-center" style={{ padding: '24px' }}>
                <Loader2 size={16} className="animate-spin" />
                <span style={{ marginLeft: '8px', fontSize: 'var(--font-size-14)' }}>Carregando histórico...</span>
              </div>
            ) : history.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {history
                  .sort((a, b) => new Date(b.createdAt ?? b.updatedAt ?? 0).getTime() - new Date(a.createdAt ?? a.updatedAt ?? 0).getTime())
                  .map((item, index) => (
                    <div key={item.id} className="d-flex gap-3" style={{ position: 'relative' }}>
                      {index < history.length - 1 && (
                        <div style={{
                          position: 'absolute',
                          left: '15px',
                          top: '32px',
                          bottom: '-12px',
                          width: '2px',
                          backgroundColor: 'var(--color-gainsboro-100)'
                        }} />
                      )}
                      
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: getHistoryColor(item),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        zIndex: 1,
                        border: '2px solid white',
                        boxShadow: '0 0 0 2px var(--color-gainsboro-100)'
                      }}>
                        {getHistoryIcon(item)}
                      </div>
                      
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <div className="d-flex justify-between align-center mb-1">
                          <span style={{ fontWeight: 600, fontSize: 'var(--font-size-14)', color: getHistoryColor(item) }}>
                            {getHistoryTitle(item)}
                          </span>
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {formatDate(item.createdAt ?? item.updatedAt ?? '')}
                          </span>
                        </div>
                        
                        <div className="d-flex align-center gap-2 mb-1">
                          <User2 size={12} color="var(--color-darkgray)" />
                          <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            Por: <strong>{item.updatedBy}</strong>
                            {item.userInfo?.email && <span> ({item.userInfo.email})</span>}
                          </span>
                        </div>

                        {/* status_change: De → Para */}
                        {item.action === 'status_change' && item.previousStatus && (
                          <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                            De: {getStatusLabel(item.previousStatus)} → {getStatusLabel(item.status ?? '')}
                          </div>
                        )}

                        {/* field_update: valor antigo → novo */}
                        {item.action === 'field_update' && (
                          <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>
                            {item.oldValue ?? '—'} → <strong>{item.newValue ?? '—'}</strong>
                          </div>
                        )}
                        
                        {item.reason && (
                          <div style={{ 
                            fontSize: 'var(--font-size-12)', 
                            color: 'var(--color-darkgray)',
                            marginTop: '4px',
                            padding: '6px 8px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            border: '1px solid var(--color-gainsboro-100)'
                          }}>
                            <strong>Motivo:</strong> {item.reason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center" style={{ padding: '24px', color: 'var(--color-darkgray)' }}>
                <History size={32} style={{ marginBottom: '12px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>
                  Nenhum histórico disponível
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showErrorAlert && (
        <PermissionAlert 
          message={errorMessage} 
          onClose={() => setShowErrorAlert(false)} 
        />
      )}

      {showCompletionModal && (
        <BusCompletionModal
          onClose={() => {
            setShowCompletionModal(false);
            setNewStatus(checkout?.status || '');
          }}
          onConfirm={handleCompletionConfirm}
          isLoading={updating}
          hasReturn={!!checkout?.volta_trip_id}
          passengers={checkout?.passengers?.map(p => ({ id: p.id, name: p.name })) || []}
        />
      )}
    </div>
  );
};

export default BusEmissionDetail;
